import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { isNil } from 'lodash';
import { Observable, of } from 'rxjs';
import {
  ClearUserRestriction as ClearUserRestrictions,
  NotifyUserRestriction,
} from 'src/app/auth/state/auth/auth.actions';
import {
  PremiumAccessDialogComponent,
  PremiumAccessDialogData,
} from 'src/app/dialogs/components/premium-access-dialog/premium-access-dialog.component';
import { TrialExpiredDialogComponent } from 'src/app/dialogs/components/trial-expired-dialog/trial-expired-dialog.component';
import { DialogIds } from 'src/app/dialogs/models/dialog-ids';
import { ContentViewService } from '../../content-views.service';
import { PendoService } from '../../pendo.service';
import { pricingConfig } from './data/pricing-content-config';
import { CalloutContentModel } from './models/callout-content.model';
import { CalloutDisplayEnum } from './models/callout-display.enum';
import { UserRestrictionEnum } from './models/user-restriction.enum';

const pendoEventNameMap = {
  [UserRestrictionEnum.BookmarkViewLimitReached]: 'View gate',
  [UserRestrictionEnum.BookmarkSaveLimitReached]: 'Save gate',
  [UserRestrictionEnum.Publish]: 'Publish gate',
};

@Injectable({
  providedIn: 'root',
})
export class UserAccessService {
  constructor(
    private store: Store,
    private dialog: MatDialog,
    private pendoService: PendoService,
    private contentViewService: ContentViewService,
    private router: Router,
  ) {}

  openPastDue() {
    this.store.dispatch(
      new NotifyUserRestriction(
        UserRestrictionEnum.ProAccountPastDue,
        CalloutDisplayEnum.Notification,
      ),
    );
  }

  openBookmarkViewWarning(viewCount: number, viewLimit: number) {
    this.store.dispatch(
      new NotifyUserRestriction(
        UserRestrictionEnum.BookmarkViewThresholdReached,
        CalloutDisplayEnum.Footer,
        {
          title: `You've viewed **${viewCount} of ${viewLimit}** models this month.`,
          actionText: 'Get Unlimited Acccess',
        },
      ),
    );
  }

  openBookmarkViewLimit() {
    return this.openPremiumAccessDialog(UserRestrictionEnum.BookmarkViewLimitReached, {}, false);
  }

  openBookmarkSaveWarning(saveCount: number, saveLimit: number) {
    this.store.dispatch(
      new NotifyUserRestriction(
        UserRestrictionEnum.BookmarkSaveThresholdReached,
        CalloutDisplayEnum.Footer,
        {
          title: `You've saved **${saveCount} of ${saveLimit}** free models.`,
          actionText: 'Get Unlimited Storage',
        },
      ),
    );
  }

  openBookmarkSaveLimit() {
    return this.openPremiumAccessDialog(UserRestrictionEnum.BookmarkSaveLimitReached);
  }

  openPublishPaywall() {
    return this.openPremiumAccessDialog(UserRestrictionEnum.Publish);
  }

  openTrialExpired() {
    const options: MatDialogConfig<any> = {
      id: DialogIds.TrialExpiredDialog,
      panelClass: 'bg-dialog-responsive',
    };
    return this.dialog.open(TrialExpiredDialogComponent, options).afterClosed();
  }

  openPremiumAccessDialog(
    type: UserRestrictionEnum,
    extendedConfig: Partial<CalloutContentModel> = {},
    dismissable: boolean = true,
  ): Observable<boolean> {
    const dialogData: PremiumAccessDialogData = {
      // base callout config
      ...pricingConfig[type],
      // extended config
      ...extendedConfig,
    };

    // Note: special case, ViewLimit always takes precedence, ignore other dialogs
    const isViewLimitOpen =
      this.getOpenPremiumDialogType() === UserRestrictionEnum.BookmarkViewLimitReached;
    if (isViewLimitOpen) {
      return of(undefined);
    } else if (type === UserRestrictionEnum.BookmarkViewLimitReached) {
      this.clearDialogs();
    }

    this.pendoService.track(pendoEventNameMap[type], {
      page: this.router.url,
      platform: this.contentViewService.getProductType(),
    });

    const dialogId = this.getPremiumDialogId(type);

    return this.dialog
      .open(PremiumAccessDialogComponent, {
        id: dialogId,
        backdropClass: dismissable ? undefined : 'bd-dialog-backdrop-headless',
        panelClass: 'bg-dialog-docked',
        disableClose: !dismissable,
        data: dialogData,
      })
      .afterClosed();
  }

  clearDialogs() {
    this.dialog.closeAll();
  }

  clearUserNotifications(...displays: CalloutDisplayEnum[]) {
    this.store.dispatch(new ClearUserRestrictions(displays));
  }

  private getPremiumDialogId(type: UserRestrictionEnum): string {
    return `${DialogIds.PremiumAccessDialog}_${type}`;
  }

  getOpenPremiumDialogType(): UserRestrictionEnum {
    return <UserRestrictionEnum>Object.values(UserRestrictionEnum).find(
      (type: UserRestrictionEnum) => {
        const dialogId = this.getPremiumDialogId(type);
        const dialog = this.dialog.getDialogById(dialogId);
        return !isNil(dialog);
      },
    );
  }
}
